import { TFunction } from "i18next";
import { DateTime, Interval } from "luxon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
	AssetCategory,
	DeviceCategory,
	LocationResource,
	LocationType,
	MODIFY_SPECIFIC_ASSET_UNPAIR_DEVICE_ID,
	NormalizedDeviceHardware,
	VelavuAPI,
	VelavuDevice,
	VelavuRoute,
	isDeviceGateway,
	normalizeDeviceHardware,
} from "velavu-js-api";
import { useDateRange } from "../../../../data/date-range";
import UnpairAssetTag from "../../../../data/unpair-asset-tag";
import IconDelete from "../../../../dynamicicons/icon-delete";
import IconMove from "../../../../dynamicicons/icon-move";
import IconPin from "../../../../dynamicicons/icon-pin";
import IconPlace from "../../../../dynamicicons/icon-place";
import DatePicker from "../../../../elements/date-picker";
import DeviceIcon from "../../../../elements/device-icon";
import Divider from "../../../../elements/divider";
import DropdownPanelToolbar from "../../../../elements/dropdown-panel-toolbar";
import EventsDetailList from "../../../../elements/events-detail-list";
import { useProvideContentRefreshable } from "../../../../elements/refresh-context";
import VelavuAlertModal from "../../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalActions from "../../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuAlertModalContent from "../../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalTextConfirm from "../../../../elements/velavu-alert-modal/velavu-alert-modal-text-confirm";
import VelavuAlertModalTitle from "../../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuButton from "../../../../elements/velavu-button";
import VelavuItemIcon, {
	VelavuItemIconData,
} from "../../../../elements/velavu-item-icon";
import VelavuMenu, { VelavuMenuItem } from "../../../../elements/velavu-menu";
import { useVelavuModal } from "../../../../elements/velavu-modal";
import VelavuTabSwitcher from "../../../../elements/velavu-tab-switcher";
import { VelavuTitledModal } from "../../../../elements/velavu-titled-modal";
import ToastNotification from "../../../../elements/toast-notification";
import { useVelavuToast } from "../../../../elements/velavu-toast";
import { useCancellableEffect } from "../../../../helper/hook-helper";
import { makeStyleable } from "../../../../helper/icon-helper";
import {
	formatDateTime,
	mapDeviceModelCategory,
	mapLocationType,
} from "../../../../helper/language-helper";
import { classArr } from "../../../../helper/style-helper";
import useMenuAnchorState from "../../../../state/menu-anchor-state";
import whitelabel from "../../../../whitelabel";
import { AnalyticsLocationState } from "../../../analytics/analytics";
import DetailModal from "../../../asset-tag-detail/detail-modal";
import { useHasPermission } from "../../../sign-in-context/sign-in-context";
import LocationDescription from "../location-description";
import * as styles from "./device-detail.module.scss";
import DeviceEnvironment from "./device-environment";
import DeviceProperties from "./device-properties";
import DeviceRoutes from "./device-routes";
import { useAssetImageURLCache } from "../../../main/asset-image-url-cache-provider";
import { useDeviceModels } from "../../../main/device-model-provider";
import IconCircleDelete from "../../../../dynamicicons/icon-circle-delete";
import { useGlobalEmitter } from "../../../../emitter/emitter-context";

export enum DeviceDetailTab {
	Info,
	TripHistory,
	Events,
	Environment,
}

interface DeviceDetailProps {
	device: VelavuDevice;
	onBack: () => void;
	onClose: () => void;

	focusDeviceOnMap: VoidFunction;

	onReposition: () => void;
	onUnpin: () => void;

	onDeleteRoute: VoidFunction;
	hoverRoute?: VelavuRoute;
	setHoverRoute: (route?: VelavuRoute) => void;
	selectedRoute?: VelavuRoute;
	setSelectedRoute: (route: VelavuRoute | undefined) => void;
	onSelectRoute: VoidFunction;

	tab: DeviceDetailTab | undefined;
	setTab: (tab: DeviceDetailTab) => void;
}

export default function DeviceDetail(props: DeviceDetailProps) {
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();
	const {
		device,
		onBack,
		onClose,
		focusDeviceOnMap,
		onDeleteRoute,
		hoverRoute,
		setHoverRoute,
		selectedRoute,
		setSelectedRoute,
		onSelectRoute,
		tab,
		setTab,
	} = props;
	const asset = device.asset;
	const assetID = asset?.id;

	//Load permissions
	const canWriteDevices = useHasPermission("devices:write");
	const canManageDevices = useHasPermission("devices:manage");
	const canWriteAssets = useHasPermission("assets:write");
	const canDeleteAssets = useHasPermission("assets:delete");
	const canDeleteDevices = useHasPermission("devices:delete");
	const canReadEvents = useHasPermission("events:read");

	const { getDeviceModel } = useDeviceModels();

	//Resolve the role this device can be switched to, if any
	const switchRoleCategory = useMemo((): DeviceCategory | undefined => {
		//Resolve the model's available categories
		const modelCategories = getDeviceModel(device.model)?.categories;
		if (modelCategories === undefined || modelCategories.length < 2) {
			return undefined;
		}

		//Return the category that is not the current one
		return modelCategories.find((category) => category !== device.category);
	}, [device, getDeviceModel]);

	//Load capabilities
	const canReposition =
		canWriteDevices &&
		(props.device.category === DeviceCategory.Anchor ||
			isDeviceGateway(props.device));
	const canUnpin =
		canManageDevices &&
		props.device.location?.location_type === LocationType.Fixed;
	//Switching to an anchor unpairs the device's asset
	const canSwitchRole =
		switchRoleCategory !== undefined &&
		canWriteDevices &&
		(switchRoleCategory === DeviceCategory.Tag || canWriteAssets);
	const canDeleteAsset = canDeleteAssets && asset !== undefined;
	const canUnregisterTag = canDeleteDevices;
	const canUseOverflowMenu =
		canReposition ||
		canUnpin ||
		canSwitchRole ||
		canDeleteAsset ||
		canUnregisterTag;

	const [routes, setRoutes] = useState<VelavuRoute[] | undefined>(undefined);

	const [sortNew, setSortNew] = useState(true);

	//Last week -> Today
	const [dateRange, setDateRange] = useDateRange(7);

	const { pushModal } = useVelavuModal();

	const deleteRoute = useCallback(
		(deletedRoute: VelavuRoute) => {
			if (assetID === undefined) {
				return;
			}

			pushModal<boolean>((resolve) => (
				<VelavuAlertModal>
					<VelavuAlertModalTitle>
						{t("dashboard.asset_detail.delete_route.title")}
					</VelavuAlertModalTitle>

					<VelavuAlertModalContent>
						{t("dashboard.asset_detail.delete_route.body")}
					</VelavuAlertModalContent>

					<VelavuAlertModalActions>
						<VelavuButton
							label={t("common.cancel")}
							variant="outlined"
							fullWidth
							onClick={() => resolve(false)}
						/>

						<VelavuButton
							label={t("common.delete")}
							variant="danger"
							fullWidth
							onClick={() => resolve(true)}
						/>
					</VelavuAlertModalActions>
				</VelavuAlertModal>
			)).then((result) => {
				if (result) {
					VelavuAPI.assetRoutes
						.deleteSpecificRoute(assetID, deletedRoute.id)
						.then(() =>
							setRoutes(
								routes?.filter(
									(route) => route.id != deletedRoute.id,
								),
							),
						)
						.finally(onDeleteRoute)
						.catch(() =>
							alert(
								t("dashboard.asset_detail.delete_route.failed"),
							),
						);
				}
			});
		},
		[assetID, onDeleteRoute, pushModal, routes, t],
	);

	const selectRoute = useCallback(
		(route: VelavuRoute | undefined) => {
			setSelectedRoute(route);
			onSelectRoute();
		},
		[onSelectRoute, setSelectedRoute],
	);

	useCancellableEffect(
		(addPromise) => {
			if (assetID === undefined || tab !== DeviceDetailTab.TripHistory)
				return;

			setRoutes(undefined);

			addPromise(
				VelavuAPI.assetRoutes.getAllRoutes(assetID, {
					since: dateRange.start,
					until: dateRange.end,
				}),
			)
				.then((routes) => setRoutes(routes))
				.catch(console.log);
		},
		[assetID, dateRange, setRoutes, tab],
	);
	const refreshRoutes = useCallback(async () => {
		//Only load routes on the trip history tab
		if (assetID === undefined || tab !== DeviceDetailTab.TripHistory) {
			return;
		}

		await VelavuAPI.assetRoutes
			.getAllRoutes(assetID, {
				since: dateRange.start,
				until: dateRange.end,
			})
			.then((routes) => setRoutes(routes));
	}, [tab, dateRange, assetID]);
	useProvideContentRefreshable(refreshRoutes);

	const sortedRoutes = useMemo(() => {
		return routes?.sort(sortNew ? compareRouteDesc : compareRouteAsc);
	}, [routes, sortNew]);

	const selectedTab = useMemo(() => {
		return tab ?? DeviceDetailTab.Info;
	}, [tab]);

	useEffect(() => {
		if (
			selectedTab !== DeviceDetailTab.TripHistory &&
			selectedRoute !== undefined
		) {
			selectRoute(undefined);
		}
	}, [selectedRoute, selectRoute, selectedTab]);

	const inspectAsset = useCallback(() => {
		pushModal(
			(close) => (
				<VelavuTitledModal
					title={t("dashboard.asset_detail.modal_title")}
					onClose={close}
				>
					<DetailModal
						asset={asset}
						device={device}
						onClose={close}
						defaultTab={asset ? "asset" : "device"}
					/>
				</VelavuTitledModal>
			),
			{ pinToTop: true },
		);
	}, [asset, device, pushModal, t]);

	const navigate = useNavigate();

	const navigateAnalytics = useCallback(() => {
		if (device === undefined) {
			return;
		}
		let state: AnalyticsLocationState;
		if (device.category === DeviceCategory.Anchor) {
			state = { anchorID: device.id };
		} else {
			state = { tagID: device.id };
		}
		navigate("/analytics", { state });
	}, [navigate, device]);

	const [menuAnchor, openMenu, closeMenu, useWrapClose] =
		useMenuAnchorState();
	const reposition = useWrapClose(props.onReposition);
	const unpin = useWrapClose(props.onUnpin);

	const deleteAsset = useCallback(() => {
		closeMenu();
		if (assetID === undefined) {
			return;
		}

		const confirmDeleteText = t("assets.confirm_delete");
		pushModal<boolean>((resolve) => (
			<VelavuAlertModalTextConfirm
				title={t("assets.delete_modal_title", { count: 1 })}
				confirmText={confirmDeleteText}
				confirmLabel={t("common.delete")}
				cancelLabel={t("common.cancel")}
				isDanger
				onResolve={resolve}
			>
				<Trans
					i18nKey="assets.delete_confirm_instruction"
					values={{ confirmText: confirmDeleteText }}
					components={{ i: <i /> }}
					parent="span"
				/>
			</VelavuAlertModalTextConfirm>
		)).then((result) => {
			if (!result) {
				return;
			}

			VelavuAPI.assets
				.deleteSpecificAsset(assetID)
				.then(() => {
					emitter.emit("asset", {
						id: assetID,
						data: null,
					});
				})
				.catch(() =>
					alert(t("dashboard.asset_detail.delete_asset.failed")),
				);
		});
	}, [assetID, closeMenu, emitter, pushModal, t]);

	const { pushToast } = useVelavuToast();

	const switchRole = useCallback(async () => {
		closeMenu();

		//Ignore if the device can't be switched to a different role
		if (switchRoleCategory === undefined) {
			return;
		}

		//Show a confirmation modal to the user
		const roleLabel = mapDeviceModelCategory(
			device.model,
			switchRoleCategory,
			t,
		);
		const result = await pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("dashboard.asset_detail.change_role.title", {
						role: roleLabel,
					})}
				</VelavuAlertModalTitle>

				<VelavuAlertModalContent>
					{switchRoleCategory === DeviceCategory.Anchor
						? t("dashboard.asset_detail.change_role.body_anchor", {
								role: roleLabel,
							})
						: t("dashboard.asset_detail.change_role.body_tag", {
								role: roleLabel,
							})}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("dashboard.asset_detail.change_role.confirm")}
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		));
		if (!result) {
			return;
		}

		try {
			//An anchor can't be paired to an asset
			if (
				switchRoleCategory === DeviceCategory.Anchor &&
				assetID !== undefined
			) {
				await VelavuAPI.assets.modifySpecificAsset(assetID, {
					device_id: MODIFY_SPECIFIC_ASSET_UNPAIR_DEVICE_ID,
				});
			}

			const updatedDevice = await VelavuAPI.devices.modifySpecificDevice(
				device.id,
				{
					category: switchRoleCategory,
				},
			);

			emitter.emit("device", {
				id: updatedDevice.id,
				data: updatedDevice,
			});
		} catch (error) {
			console.warn("Failed to change device role", error);

			pushToast((close) => (
				<ToastNotification
					type="error"
					title={t("dashboard.asset_detail.change_role.failed_title")}
					onDismiss={close}
				>
					{t("dashboard.asset_detail.change_role.failed_body")}
				</ToastNotification>
			));
		}
	}, [
		assetID,
		closeMenu,
		device.id,
		device.model,
		emitter,
		pushModal,
		pushToast,
		switchRoleCategory,
		t,
	]);

	const unregisterTag = useCallback(() => {
		closeMenu();

		const confirmUnregisterText = t("devices.confirm_unregister");
		pushModal<boolean>((resolve) => (
			<VelavuAlertModalTextConfirm
				title={t("devices.unregister_title", { count: 1 })}
				confirmText={confirmUnregisterText}
				confirmLabel={t("common.delete")}
				cancelLabel={t("common.cancel")}
				isDanger
				onResolve={resolve}
			>
				<Trans
					i18nKey="devices.unregister_confirm_instruction"
					values={{ confirmText: confirmUnregisterText }}
					components={{ i: <i /> }}
					parent="span"
				/>
			</VelavuAlertModalTextConfirm>
		)).then((result) => {
			if (!result) {
				return;
			}

			VelavuAPI.devices
				.deleteSpecificDevice(device.id)
				.then(() => {
					emitter.emit("device", {
						id: device.id,
						data: null,
					});
				})
				.catch(() =>
					alert(t("dashboard.asset_detail.unregister_device.failed")),
				);
		});
	}, [closeMenu, device.id, emitter, pushModal, t]);

	const assetImageCache = useAssetImageURLCache();

	const itemIconData = useMemo((): VelavuItemIconData => {
		if (asset === undefined || device.category !== DeviceCategory.Tag) {
			return { model: device.model };
		} else if (asset.profile_img_url !== undefined) {
			return { url: asset.profile_img_url };
		} else {
			const cachedURL = assetImageCache.getAssetImageURL(asset.id);
			if (cachedURL) {
				return { url: cachedURL };
			} else {
				return { category: asset.category };
			}
		}
	}, [asset, device, assetImageCache]);

	return (
		<>
			{canUseOverflowMenu && (
				<VelavuMenu
					open={!!menuAnchor}
					onClose={closeMenu}
					refPosition={menuAnchor}
				>
					{canReposition && (
						<VelavuMenuItem
							label={t("common.reposition")}
							icon={makeStyleable(IconMove)}
							onClick={reposition}
						/>
					)}
					{canUnpin && (
						<VelavuMenuItem
							label={t("common.unpin")}
							icon={makeStyleable(IconPin)}
							onClick={unpin}
						/>
					)}
					{canSwitchRole && (
						<VelavuMenuItem
							label={
								switchRoleCategory === DeviceCategory.Anchor
									? t(
											"dashboard.asset_detail.change_role.action_anchor",
										)
									: t(
											"dashboard.asset_detail.change_role.action_tag",
										)
							}
							icon={(size, color) => (
								<DeviceIcon
									size={size}
									color={color}
									model={device.model}
								/>
							)}
							onClick={switchRole}
						/>
					)}
					{canDeleteAsset && (
						<VelavuMenuItem
							label={t("common.delete_asset")}
							icon={makeStyleable(IconDelete)}
							onClick={deleteAsset}
							danger
						/>
					)}
					{canUnregisterTag && (
						<VelavuMenuItem
							label={t("common.unregister_device")}
							icon={makeStyleable(IconCircleDelete)}
							onClick={unregisterTag}
							danger
						/>
					)}
				</VelavuMenu>
			)}

			<div className={styles.container}>
				<DropdownPanelToolbar
					onBack={onBack}
					onClose={onClose}
					onDetail={canUseOverflowMenu ? openMenu : undefined}
					detailOpen={!!menuAnchor}
				/>

				<div className={styles.header} onClick={focusDeviceOnMap}>
					<VelavuItemIcon data={itemIconData} />
					<div className={styles.headerText}>
						<span className={styles.headerName}>
							{asset?.name ?? device.id}
						</span>
						<LocationDescription device={device} />
						{device.location !== undefined &&
							device.location.location_type !==
								LocationType.Fixed && (
								<DeviceLastSeen
									location={device.location}
									dynamicPosition={
										device.config?.positioning?.dynamic_mode
									}
								/>
							)}
					</div>
					<IconPlace
						className={styles.headerIcon}
						color={whitelabel.palette.secondary700}
					/>
				</div>

				<VelavuTabSwitcher
					selectedKey={selectedTab}
					onSelectKey={setTab}
					labels={{
						[DeviceDetailTab.Info]: t("dashboard.tabs.info"),
						[DeviceDetailTab.TripHistory]:
							asset !== undefined &&
							normalizeDeviceHardware(device.hardware) ===
								NormalizedDeviceHardware.Argo &&
							t("dashboard.tabs.trip_history"),
						[DeviceDetailTab.Events]:
							canReadEvents && t("dashboard.tabs.events"),
						[DeviceDetailTab.Environment]:
							device.environment !== undefined &&
							t("dashboard.tabs.environment"),
					}}
				>
					{selectedTab === DeviceDetailTab.Info && (
						<div className={styles.tables}>
							<DeviceProperties device={device} />
							<div className={styles.detailsRow}>
								<VelavuButton
									className={styles.detailsButton}
									label={t("common.view_details")}
									fullWidth
									onClick={inspectAsset}
								/>

								{!isDeviceGateway(props.device) && (
									<VelavuButton
										className={styles.detailsButton}
										label={t("common.analytics")}
										fullWidth
										onClick={navigateAnalytics}
										variant="outlined"
									/>
								)}
							</div>
						</div>
					)}

					{selectedTab === DeviceDetailTab.TripHistory && (
						<>
							<DatePicker
								range={dateRange}
								onUpdate={setDateRange}
								sortNew={sortNew}
								setSortNew={setSortNew}
							/>
							<Divider />
							<div className={styles.listScroll}>
								<DeviceRoutes
									routes={sortedRoutes}
									onHoverRoute={setHoverRoute}
									hoveredRouteID={hoverRoute?.id}
									onSelectRoute={setSelectedRoute}
									selectedRoute={selectedRoute}
									onDeleteRoute={deleteRoute}
								/>
							</div>
						</>
					)}

					{selectedTab === DeviceDetailTab.Events &&
						(assetID !== undefined ? (
							<EventsDetailList assetID={assetID} />
						) : (
							<EventsDetailList deviceID={device.id} />
						))}

					{selectedTab === DeviceDetailTab.Environment && (
						<DeviceEnvironment device={device} />
					)}
				</VelavuTabSwitcher>
			</div>
		</>
	);
}

function compareRouteAsc(route1: VelavuRoute, route2: VelavuRoute) {
	return (
		new Date(route1.data.time_start).getTime() -
		new Date(route2.data.time_start).getTime()
	);
}

function compareRouteDesc(route1: VelavuRoute, route2: VelavuRoute) {
	return -compareRouteAsc(route1, route2);
}

interface FormattedLastSeen {
	short: string;
	long: string;
}

function formatLastSeen(date: DateTime, t: TFunction): FormattedLastSeen {
	const now = DateTime.now();
	const interval = Interval.fromDateTimes(date, now);
	const duration = interval.toDuration(["minutes", "hours", "days"]);
	const long = date
		.setLocale("en-US")
		.toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS);

	if (duration.minutes < 1 && duration.hours < 1 && duration.days < 1) {
		return {
			short: t("dashboard.time.just_now"),
			long: long,
		};
	}
	if (duration.hours < 1 && duration.days < 1) {
		const minutes = Math.floor(duration.minutes);
		return {
			short: t("dashboard.time.minutes_ago", { count: minutes }),
			long: long,
		};
	} else if (duration.hours < 24 && duration.days < 1) {
		const hours = Math.floor(duration.hours);
		return {
			short: t("dashboard.time.hours_ago", { count: hours }),
			long: long,
		};
	} else if (duration.days < 7) {
		const days = Math.floor(duration.days);
		return {
			short: t("dashboard.time.days_ago", { count: days }),
			long: long,
		};
	} else {
		return {
			short: formatDateTime(date),
			long: long,
		};
	}
}

function DeviceLastSeen(props: {
	location: LocationResource;
	dynamicPosition?: boolean;
}) {
	const { t } = useTranslation();
	const { location, dynamicPosition } = props;
	const { short, long } = useMemo(
		() => formatLastSeen(DateTime.fromISO(location.timestamp), t),
		[location.timestamp, t],
	);
	const locationType = useMemo(
		() => mapLocationType(location.location_type, t),
		[location.location_type, t],
	);

	return (
		<span className={classArr(styles.headerLastSeen)} title={long}>
			{`${
				dynamicPosition ? t("dashboard.time.last_moved_prefix") : ""
			}${short} • ${locationType}`}
		</span>
	);
}
